const FREE_VARIANT_ID = 49604481712418;
const FREE_GIFT_PROPERTY = '_free_gift';
let isUpdating = false;
let updatePending = false;

async function getCart() {
  const res = await fetch('/cart.js');
  return res.json();
}

async function addFreeGift() {
  await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      items: [{
        id: FREE_VARIANT_ID,
        quantity: 1,
        properties: { [FREE_GIFT_PROPERTY]: 'yes' }
      }]
    })
  });
}

async function removeFreeGift(lineKey) {
  // Target the line by its unique key, not its position in the array.
  // Position can shift between when you read the cart and when this
  // call actually lands — key can't.
  await fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: lineKey, quantity: 0 })
  });
}

function hideFreeGiftControls() {
  // NOTE: these selectors are generic guesses (table-row / cart-item /
  // data-variant-id). If the delete button or qty stepper still shows up
  // on the free line, open dev tools on your actual cart page, grab the
  // real class names / element structure for one cart row, and send them
  // to me — I'll swap these for the exact match.
  const allItems = document.querySelectorAll(
    '.cart-items__table-row, .cart-item, [data-variant-id]'
  );

  allItems.forEach(item => {
    const variantId = item.dataset.variantId ||
      item.querySelector('[data-variant-id]')?.dataset.variantId;

    if (variantId == FREE_VARIANT_ID) {
      const deleteBtn = item.querySelector(
        '.cart-remove-button, [data-action="remove"], button[aria-label*="Remove"], button[aria-label*="Delete"], .cart-items__remove, .quantity__button--minus, .cart-items__quantity'
      );
      if (deleteBtn) deleteBtn.style.display = 'none';

      const quantityWrapper = item.querySelector(
        '.quantity, .cart-items__quantity-wrapper, quantity-selector-component'
      );
      if (quantityWrapper) quantityWrapper.style.display = 'none';

      item.querySelectorAll('button').forEach(btn => {
        if (
          btn.innerHTML.includes('icon-delete') ||
          btn.innerHTML.includes('icon-trash') ||
          btn.getAttribute('aria-label')?.toLowerCase().includes('remov') ||
          btn.getAttribute('aria-label')?.toLowerCase().includes('delet')
        ) {
          btn.style.display = 'none';
        }
      });
    }
  });
}

// THE ACTUAL FIX: Horizon-family themes don't react to a plain
// CustomEvent('cart:refresh') — their cart-drawer-component /
// cart-items-component only re-render off Horizon's own CartUpdateEvent
// class, imported from the theme's own JS bundle. This is what actually
// tells the cart page "the data changed, re-fetch and re-render yourself."
function notifyThemeCartUpdate(cart) {
  import('@theme/events')
    .then(({ CartUpdateEvent }) => {
      const event = new CartUpdateEvent(cart, 'free-gift-automation', {
        itemCount: cart.item_count,
        source: 'free-gift-automation',
        sections: {}
      });
      document.dispatchEvent(event);
    })
    .catch(() => {
      // Fallback in case this ever runs on a non-Horizon theme
      const detail = { cart };
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail }));
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail }));
    });
}

async function updateFreeGift() {
  if (isUpdating) { updatePending = true; return; }
  isUpdating = true;

  try {
    let cart = await getCart();

    const freeGiftItem = cart.items.find(
      item => item.variant_id == FREE_VARIANT_ID &&
      item.properties?.[FREE_GIFT_PROPERTY]
    );

    // Excludes the Lip Oil itself (paid or free) from counting toward the
    // "buy something" requirement — buying only the Lip Oil should never
    // add a second, free one. This mirrors how the Shopify discount is
    // scoped (it also requires a genuinely different product in cart).
    const nonFreeItems = cart.items.filter(
      item => item.variant_id != FREE_VARIANT_ID
    );

    const hasQualifyingItems = nonFreeItems.length > 0;
    let changed = false;

    if (hasQualifyingItems && !freeGiftItem) {
      await addFreeGift();
      changed = true;
    } else if (!hasQualifyingItems && freeGiftItem) {
      await removeFreeGift(freeGiftItem.key);
      changed = true;
    }

    if (changed) {
      cart = await getCart(); // re-fetch so the theme gets the true final state
      notifyThemeCartUpdate(cart);
    }

    setTimeout(hideFreeGiftControls, 150);
    setTimeout(hideFreeGiftControls, 600);

  } catch (err) {
    console.error('Free gift error:', err);
  } finally {
    isUpdating = false;
    // If a change came in while we were mid-check, run once more instead
    // of silently dropping it.
    if (updatePending) {
      updatePending = false;
      updateFreeGift();
    }
  }
}

// Watches every cart-changing call your theme (or any app) makes, then
// re-checks immediately after. No artificial delay needed — by the time
// the intercepted fetch resolves, the mutation has already completed
// server-side.
const originalFetch = window.fetch;
window.fetch = async function (...args) {
  const result = await originalFetch.apply(this, args);
  const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
  if (
    url.includes('/cart/add') ||
    url.includes('/cart/change') ||
    url.includes('/cart/update')
  ) {
    updateFreeGift();
  }
  return result;
};

// Debounced catch-all: re-hide the gift's controls if the theme re-renders
// the cart DOM after our correction (e.g. once CartUpdateEvent lands).
let hideDebounce;
const observer = new MutationObserver(() => {
  clearTimeout(hideDebounce);
  hideDebounce = setTimeout(hideFreeGiftControls, 100);
});
observer.observe(document.body, { childList: true, subtree: true });

document.addEventListener('DOMContentLoaded', () => {
  updateFreeGift();
  setTimeout(hideFreeGiftControls, 500);
});

// EXTRA SAFETY NET — covers the split-second timing gap where someone
// clicks checkout before the removal above has finished. This is NOT a
// substitute for scoping the automatic discount correctly: a shopper with
// JavaScript disabled can bypass this entirely. The discount scope is
// what actually protects your revenue; this just protects normal
// shoppers from an honest race condition.
function cartHasOnlyGift(cart) {
  const giftLine = cart.items.find(item =>
    item.variant_id == FREE_VARIANT_ID && item.properties?.[FREE_GIFT_PROPERTY]
  );
  const qualifyingCount = cart.items.filter(item =>
    item.variant_id != FREE_VARIANT_ID
  ).length;
  return qualifyingCount === 0 && !!giftLine;
}

async function guardCheckout() {
  const cart = await getCart();
  if (cartHasOnlyGift(cart)) {
    const giftLine = cart.items.find(item =>
      item.variant_id == FREE_VARIANT_ID && item.properties?.[FREE_GIFT_PROPERTY]
    );
    await removeFreeGift(giftLine.key);
  }
  window.location.href = '/checkout';
}

// Covers the standard Shopify cart form checkout button
// (button/input name="checkout") used by virtually all themes, plus any
// plain link pointing straight at /checkout.
document.addEventListener('click', function (e) {
  const trigger = e.target.closest(
    'button[name="checkout"], input[name="checkout"], a[href="/checkout"], a[href^="/checkout?"]'
  );
  if (!trigger) return;
  e.preventDefault();
  guardCheckout();
}, true);

document.addEventListener('submit', function (e) {
  if (e.submitter && e.submitter.name === 'checkout') {
    e.preventDefault();
    guardCheckout();
  }
}, true);